/**
 * Connectivity — single source of truth for "are we really online?".
 *
 * `navigator.onLine` is unreliable inside a WebView (especially iOS after
 * suspension), and the Capacitor Network plugin's events can lag. We treat the
 * device as online only when navigator says so AND a lightweight reachability
 * probe to our own API succeeds. Callers subscribe via onChange().
 *
 * The Capacitor Network plugin is imported lazily/defensively so this module
 * works unchanged in a plain browser (where the plugin is absent).
 */
import { Capacitor } from "@capacitor/core";
import { apiUrl } from "@/lib/env";

type Listener = (online: boolean) => void;

/** True inside the packaged iOS/Android shell. On native, `navigator.onLine` is
 *  unreliable: GMS-less / Huawei / captive-portal-restricted devices can't reach
 *  Android's network-validation endpoint and so report `navigator.onLine === false`
 *  while the connection works perfectly. We must therefore NEVER treat it as an
 *  authoritative "offline" signal on native — the reachability probe (or the
 *  Capacitor Network plugin) is the source of truth there instead. */
const IS_NATIVE = Capacitor.isNativePlatform();

/** The ONLY case where `navigator.onLine` may be trusted to mean "definitely
 *  offline": web builds. On native it lies (see IS_NATIVE), so this is always
 *  false there and callers fall through to the real probe. */
function navigatorDefinitelyOffline(): boolean {
  return !IS_NATIVE && typeof navigator !== "undefined" && !navigator.onLine;
}

let listeners = new Set<Listener>();
// On native start optimistic (true) — navigator.onLine may be a false negative;
// the first probe corrects it within seconds.
let lastKnown = IS_NATIVE ? true : typeof navigator !== "undefined" ? navigator.onLine : true;

/** Authoritative reachability flag (updated by probeReachable + readThrough
 *  success/failure + browser events). More reliable than navigator.onLine,
 *  which can report "online" while every request fails. */
let reachable = IS_NATIVE ? true : typeof navigator !== "undefined" ? navigator.onLine : true;
/** When `reachable` was last confirmed (ms epoch). Drives the staleness TTL so
 *  a belief of "online" can't persist after the network silently drops. */
let reachableCheckedAt = 0;
export function isReachable(): boolean { return reachable; }
export function markReachable(value: boolean): void { reachable = value; reachableCheckedAt = Date.now(); }

/** Offline support is for vessel + shore_staff. Admin / super_admin are
 *  online-only: they bypass the Dexie cache entirely (no read-through, no
 *  mirror), so they always see live server data and never a stale local copy.
 *  Set by OfflineProvider from the signed-in user's role. Default OFF so a
 *  pre-login / no-user state never touches the cache. */
let offlineEnabled = false;
export function setOfflineEnabled(value: boolean): void { offlineEnabled = value; }
export function isOfflineEnabled(): boolean { return offlineEnabled; }

function emit(next: boolean) {
  if (next === lastKnown) return;
  lastKnown = next;
  for (const l of listeners) l(next);
}

/** Best-effort reachability probe: HEAD our health endpoint with a short
 *  timeout. Returns false on any network failure/timeout. */
export async function probeReachable(timeoutMs = 4000): Promise<boolean> {
  if (navigatorDefinitelyOffline()) { reachable = false; return false; }
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(apiUrl("/api/healthz"), { method: "GET", signal: ctrl.signal, credentials: "include" });
    clearTimeout(t);
    // ANY HTTP response means the server is reachable — including 401
    // (unauthenticated), 429 (rate-limited), or 5xx (server erroring but up).
    // Only a genuine network failure/timeout (the catch below) is "offline".
    // (Previously a 429 wrongly flipped the whole app to Offline.)
    void res;
    markReachable(true);
    return reachable;
  } catch {
    markReachable(false);
    return false;
  }
}

/** Synchronous best-guess (no probe) — fine for gating UI affordances. */
export function isOnline(): boolean {
  // navigator.onLine is unreliable on native (see IS_NATIVE) — defer to the
  // probe-authoritative `reachable` flag there instead of a false negative.
  if (IS_NATIVE) return reachable;
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

/**
 * DEVICE-level connectivity — does the device have a network interface up
 * (Wi-Fi / cellular), independent of whether OUR server is reachable. On native
 * this uses the Capacitor Network plugin, which is reliable, unlike
 * `navigator.onLine` inside an Android/iOS WebView (which frequently reports
 * `false` while the device is actually online). Used to tell "device offline"
 * apart from "server unreachable" in user-facing messages (e.g. on the login
 * screen, which runs before OfflineProvider is mounted).
 */
export async function isDeviceConnected(): Promise<boolean> {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (Capacitor.isNativePlatform()) {
      const { Network } = await import("@capacitor/network");
      const status = await Network.getStatus();
      return status.connected;
    }
  } catch {
    /* plugin missing / web build — fall back to navigator.onLine */
  }
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

// Reachability with a short staleness TTL. readThrough awaits this before EVERY
// read decision: if our last confirmation is recent we trust it (no extra
// request); otherwise we re-probe. This means online reads stay fast/fresh while
// an offline transition is detected within the TTL — so reads go STRAIGHT to
// Dexie instead of firing a doomed request and only then falling back.
const REACHABILITY_TTL_MS = 8000;
let inFlightProbe: Promise<boolean> | null = null;
export function ensureFreshReachability(maxAgeMs = REACHABILITY_TTL_MS): Promise<boolean> {
  // navigator.onLine === false is an authoritative offline signal (the OS/WebView
  // has no network). Trust it IMMEDIATELY — even inside the TTL — so reads go
  // straight to cache instead of firing one doomed request. DevTools "Offline"
  // flips navigator.onLine but doesn't always fire the `offline` event (or fires
  // it before our listener registers), which would otherwise leave `reachable`
  // stale-true until the TTL lapses and surface a spurious request per key.
  if (navigatorDefinitelyOffline()) {
    if (reachable) markReachable(false);
    return Promise.resolve(false);
  }
  if (reachableCheckedAt !== 0 && Date.now() - reachableCheckedAt < maxAgeMs) {
    return Promise.resolve(reachable);
  }
  // Coalesce concurrent probes (a page mount fires many reads at once).
  if (!inFlightProbe) {
    inFlightProbe = probeReachable().finally(() => { inFlightProbe = null; });
  }
  return inFlightProbe;
}

/** Fail fast for a SERVER-ONLY action (submit / complete / approve / reopen)
 *  when the API isn't reachable. `fetch` has no timeout, so firing such a
 *  request offline hangs forever and the caller's button stays "submitting…".
 *  Throw a clear, user-facing error instead. Does an AUTHORITATIVE probe (≤4s,
 *  instant when navigator is offline) rather than trusting the TTL-cached flag,
 *  so a "server down / captive portal" drop can't slip through a stale window. */
export async function assertReachable(
  message = "You're offline — reconnect and try again.",
): Promise<void> {
  if (!(await probeReachable())) throw new Error(message);
}

/** Subscribe to connectivity transitions. Wires browser online/offline events
 *  plus the Capacitor Network plugin when present. Returns an unsubscribe. */
export function onConnectivityChange(listener: Listener): () => void {
  listeners.add(listener);

  const onBrowserOnline = () => emit(true);
  const onBrowserOffline = () => { reachable = false; emit(false); };
  // Web only: on native the WebView's online/offline events mirror the unreliable
  // navigator.onLine and would spuriously force offline. Native transitions come
  // from the Capacitor Network plugin listener below instead.
  if (!IS_NATIVE && typeof window !== "undefined") {
    window.addEventListener("online", onBrowserOnline);
    window.addEventListener("offline", onBrowserOffline);
  }

  // Capacitor Network plugin (native shells). Optional — absent in browsers.
  let removeCap: (() => void) | undefined;
  void (async () => {
    try {
      const mod = await import("@capacitor/network").catch(() => null);
      if (!mod?.Network) return;
      const handle = await mod.Network.addListener("networkStatusChange", (s: { connected: boolean }) => emit(s.connected));
      removeCap = () => void handle.remove();
    } catch {
      /* plugin not installed — browser-only build */
    }
  })();

  return () => {
    listeners.delete(listener);
    if (!IS_NATIVE && typeof window !== "undefined") {
      window.removeEventListener("online", onBrowserOnline);
      window.removeEventListener("offline", onBrowserOffline);
    }
    removeCap?.();
  };
}
